// «Агент → Журнал» — один журнал решений (вместо «Решения → История» и «Журнала решений»): что сделано или решено,
// кем, почему, чего ждали, итог спустя время. Фильтры: все / ваши решения / действия агента / страховка / откаты.
// У выполненного действия в рекламе — откат одной кнопкой («Включить обратно», «Вернуть бюджет», «Остановить
// снова», «Вернуть как было»): новая заявка от вашего имени, её выполнит платформа, заново сверив лимиты.
// Сверху — «Счёт агента» (что дали его решения за 30 дней).
import { Button, Chip, EmptyState, Segmented, type Tone, toast, when } from '@autoaff/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { NotebookPen, Play, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import type { JournalEntry } from '../../agent-api';
import { api } from '../../api';
import { useRefreshWaiting } from '../../shell/waiting';
import { type Launched, launchProgress } from '../LaunchPlan';
import { AgentScore } from './AgentScore';

type Who = 'all' | 'owner' | 'agent' | 'guard' | 'undo';

/** Строка журнала с тем, что api знает о заявке: комментарий владельца, откат — доступен ли и был ли. */
type Entry = Omit<JournalEntry, 'proposal'> & {
  proposal:
    | (NonNullable<JournalEntry['proposal']> & {
        ownerComment?: string | null;
        undo?: string | null;
        undoneAt?: string | null;
        undoOf?: string | null;
        /** Недоведённый запуск оффера: можно довести, что успело создаться, своя ли кампания. */
        resumable?: boolean;
        created?: Launched | null;
        ownCampaign?: boolean;
      })
    | null;
};

const FILTERS: Array<{ value: Who; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'owner', label: 'Ваши решения' },
  { value: 'agent', label: 'Действия агента' },
  { value: 'guard', label: 'Страховка' },
  { value: 'undo', label: 'Откаты' },
];

const EMPTY: Record<Who, string> = {
  all: 'Сюда попадает каждое решение агента, ваше и страховки: что сделано, почему, чего ждали — а через время агент сам проверяет, сработало ли, и дописывает итог.',
  owner: 'Ваших решений пока нет: одобрения, отказы, откаты и изменения лимитов появятся здесь.',
  agent: 'Агент пока ничего не решал и не предлагал.',
  guard: 'Страховка пока ничего не останавливала.',
  undo: 'Откатывать пока нечего: здесь появятся действия в рекламе, которые можно вернуть одной кнопкой.',
};

const ACTOR: Record<JournalEntry['actor'], { text: string; tone: Tone }> = {
  agent: { text: 'Агент', tone: 'accent' },
  owner: { text: 'Вы', tone: 'info' },
  guard: { text: 'Страховка', tone: 'warning' },
  platform: { text: 'Платформа', tone: 'default' },
};

const VERDICT: Record<string, { text: string; tone: Tone }> = {
  worked: { text: 'Сработало', tone: 'success' },
  failed: { text: 'Не сработало', tone: 'danger' },
  unclear: { text: 'Неясно', tone: 'default' },
};

const PROPOSAL: Record<string, { text: string; tone: Tone }> = {
  shadow: { text: 'Совет', tone: 'default' },
  pending: { text: 'Ждёт вас', tone: 'warning' },
  approved: { text: 'Одобрено', tone: 'success' },
  executing: { text: 'Выполняется', tone: 'info' },
  executed: { text: 'Выполнено', tone: 'success' },
  rejected: { text: 'Отклонено', tone: 'danger' },
  expired: { text: 'Истекло', tone: 'default' },
  failed: { text: 'Не выполнено', tone: 'danger' },
};

/** Откат выполненного действия в рекламе — кнопка называет, что вернётся. */
const UNDO_LABEL: Record<string, string> = {
  'meta.resume': 'Включить обратно',
  'meta.pause': 'Остановить снова',
  'meta.budget': 'Вернуть бюджет',
  'meta.api': 'Вернуть как было',
};

function UndoButton({ id, label, onDone }: { id: string; label: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    try {
      await api(`/api/agent/proposals/${id}/undo`, {});
      toast('Откат отправлен — платформа выполнит в Meta и напишет итог');
      onDone();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      icon={busy ? <span className="spinner" /> : <RotateCcw />}
      disabled={busy}
      onClick={() => void undo()}
    >
      {label}
    </Button>
  );
}

/** «Довести запуск» оффера, который Meta не довела до конца: платформа продолжит с созданного. */
function ResumeButton({ id, onDone }: { id: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      await api(`/api/agent/proposals/${id}/resume`, {});
      toast('Запуск доводится — платформа продолжит с созданного и напишет итог');
      onDone();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="primary"
      icon={busy ? <span className="spinner" /> : <Play />}
      disabled={busy}
      onClick={() => void resume()}
    >
      Довести запуск
    </Button>
  );
}

function Row({ e, onChanged }: { e: Entry; onChanged: () => void }) {
  const a = ACTOR[e.actor];
  const p = e.proposal;
  const verdict =
    p?.ownerVerdict === 'agree' ? 'вы согласны' : p?.ownerVerdict === 'disagree' ? 'вы не согласны' : null;
  // Запуск оффера, который Meta не довела до конца: что создано, «Довести запуск» и «Остановить созданное» — у
  // строки «не выполнено»
  const partial = e.action === 'failed' && p?.type === 'meta.launch' && p.status === 'failed';
  const launch = p?.type === 'meta.launch';
  // Откат — у строки самого выполнения (не у заявки и не у одобрения той же заявки)
  const undoLabel =
    ((e.action === 'executed' && p?.status === 'executed') || partial) && p?.undo && !p.undoneAt
      ? launch
        ? 'Остановить созданное'
        : UNDO_LABEL[p.undo]
      : null;
  const progress = partial ? launchProgress(p?.created, Boolean(p?.ownCampaign)) : null;
  // Комментарий к отказу уже записан причиной решения владельца — второй раз не повторяем
  const ownComment = p?.ownerComment && p.ownerComment !== e.reason ? p.ownerComment : null;
  return (
    <li className="jr">
      <time className="jr-time muted" dateTime={e.createdAt}>
        {when(e.createdAt)}
      </time>
      <div className="jr-main">
        <div className="jr-chips">
          <Chip tone={a.tone}>{a.text}</Chip>
          {p?.undoOf && <Chip tone="info">Откат</Chip>}
          {p && (
            <Chip tone={PROPOSAL[p.status]?.tone ?? 'default'}>
              {PROPOSAL[p.status]?.text ?? p.status}
              {verdict ? ` · ${verdict}` : ''}
            </Chip>
          )}
          {e.verdict && (
            <Chip tone={VERDICT[e.verdict]?.tone ?? 'default'}>{VERDICT[e.verdict]?.text ?? e.verdict}</Chip>
          )}
        </div>
        <b className="jr-title">{e.title}</b>
        {e.reason && (
          <p className="jr-why">
            {e.actor === 'owner' && e.action === 'rejected' ? 'Ваш комментарий' : 'Почему'}: {e.reason}
          </p>
        )}
        {e.expectedEffect && <p className="jr-why">Ожидание: {e.expectedEffect}</p>}
        {ownComment && <p className="jr-why">Ваш комментарий: {ownComment}</p>}
        {e.outcome ? (
          <p className="jr-outcome">Итог: {e.outcome}</p>
        ) : (
          e.reviewAt && <p className="muted jr-review">Агент проверит итог {when(e.reviewAt)}</p>
        )}
        {progress && <p className="jr-why">{progress}</p>}
        {p?.undoneAt && (e.action === 'executed' || partial) && (
          <p className="muted jr-review">Откачено {when(p.undoneAt)}</p>
        )}
        {(undoLabel || (partial && p?.resumable)) && p && (
          <div className="jr-acts">
            {partial && p.resumable && <ResumeButton id={p.id} onDone={onChanged} />}
            {undoLabel && <UndoButton id={p.id} label={undoLabel} onDone={onChanged} />}
          </div>
        )}
      </div>
    </li>
  );
}

export function JournalTab() {
  const [who, setWho] = useState<Who>('all');
  const refresh = useRefreshWaiting();
  const q = useInfiniteQuery({
    queryKey: ['agent', 'journal', who],
    queryFn: ({ pageParam }) =>
      api<Entry[]>(`/api/agent/journal?limit=40&who=${who}${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.length === 40 ? last[last.length - 1]?.id : undefined),
    refetchInterval: 30_000,
  });
  const items = q.data?.pages.flat() ?? [];
  return (
    <div className="pc jr-page">
      <AgentScore />
      <div className="jr-filter">
        <Segmented label="Показать" value={who} onChange={setWho} options={FILTERS} />
      </div>
      {q.isPending && <div className="card skeleton" style={{ height: 260 }} />}
      {q.isError && (
        <div className="card">
          <EmptyState
            icon={<NotebookPen />}
            title="Не удалось загрузить журнал"
            action={<Button onClick={() => q.refetch()}>Повторить</Button>}
          >
            {(q.error as Error).message}
          </EmptyState>
        </div>
      )}
      {q.isSuccess && items.length === 0 && (
        <div className="card">
          <EmptyState icon={<NotebookPen />} title="Здесь пока пусто">
            {EMPTY[who]}
          </EmptyState>
        </div>
      )}
      {items.length > 0 && (
        <div className="card">
          <ul className="jr-list">
            {items.map((e) => (
              <Row key={e.id} e={e} onChanged={refresh} />
            ))}
          </ul>
        </div>
      )}
      {q.hasNextPage && (
        <Button
          onClick={() => q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
          style={{ alignSelf: 'center' }}
        >
          Показать раньше
        </Button>
      )}
    </div>
  );
}
